import { useInfiniteQuery } from '@zg-rentals/react-query5';
import { RentalManagerAccountViewZrmPropertiesWebApiHandler_list } from '@zg-rentals/ts-rental-manager-api-client';

import { cleanApiParams } from '../properties/zrm';

import type {
  AccountViewListingGroupDto,
  AccountViewPropertiesResponseDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import type {
  GroupablePropertiesData,
  GroupablePropertiesListParams,
  UseGetPropertiesListOptions,
} from '../properties/zrm';

export const useGetPropertyTableList = (
  params: GroupablePropertiesListParams,
  options: UseGetPropertiesListOptions = {},
) => {
  const { enabled = true } = options;
  const { includeArchived, includeUnarchived, limit, searchText = '' } = params;

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, isFetching } =
    useInfiniteQuery<
      AccountViewPropertiesResponseDto,
      Error,
      { pages: Array<AccountViewPropertiesResponseDto> },
      Array<boolean | string | number | undefined>,
      string
    >({
      queryKey: ['getPropertyTableList', searchText, includeArchived, includeUnarchived, limit],
      queryFn: async ({ pageParam }) => {
        return RentalManagerAccountViewZrmPropertiesWebApiHandler_list(
          cleanApiParams(
            { limit, searchText, includeArchived, includeUnarchived, startKeyExclusive: pageParam || undefined },
            ['limit', 'searchText', 'includeArchived', 'includeUnarchived', 'startKeyExclusive'],
          ),
        );
      },
      initialPageParam: '',
      getNextPageParam: (lastPage) => (lastPage.pager?.hasMore ? lastPage.pager.endKey : undefined),
      enabled,
      // Keep previous data during refetch to prevent UI flicker
      placeholderData: (previousData) => {
        if (!previousData?.pages?.length) {
          return;
        }

        return previousData;
      },
      meta: {
        tags: ['getProperties', String(includeArchived)],
        context: {
          api: 'RentalManagerAccountViewZrmPropertiesWebApiHandler_list',
          details: 'useGetPropertyTableList',
        },
      },
    });

  const propertiesData: GroupablePropertiesData = data?.pages.length
    ? {
        // The property-table endpoint returns `properties` (AccountViewPropertyResponseDto), which the
        // property card components still consume as AccountViewListingGroupDto during this transition.
        listingGroups: data.pages.flatMap((page) => page.properties) as unknown as Array<AccountViewListingGroupDto>,
        listingCount: data.pages[0].listingCount,
        pager: data.pages[data.pages.length - 1].pager,
      }
    : {};

  return {
    data: propertiesData,
    error,
    isPending,
    isFetching,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};
